import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';

import { accountPersistConfig } from 'modules/account/storage/accountPersistConfig';
import { accountSlice } from 'modules/account/store/accountSlice';
import { accountTopUpPersistConfig } from 'domains/account/storage/accountTopUpPersistConfig';
import { accountTopUpSlice } from 'domains/account/store/accountTopUpSlice';
import { authPersistConfig } from 'modules/auth/storage/authPersistsConfig';
import { authSlice } from 'modules/auth/store/authSlice';
import { guardDialogSlice } from 'modules/guardDialog/store/dialogSlice';
import { i18nPersistConfig } from 'modules/i18n/storage/i18nPersistConfig';
import { i18nSlice } from 'modules/i18n/i18nSlice';
import { jwtTokenManagerPersistConfig } from 'domains/jwtToken/storage/jwtTokenManagerPersistConfig';
import { jwtTokenManagerSlice } from 'domains/jwtToken/store/jwtTokenManagerSlice';
import { projectsSlice } from 'domains/projects/store';
import { notificationSlice } from 'domains/notifications/store/notificationSlice';
import { personalAccountPersistConfig } from 'modules/personalAccount/storage/personalAccountPersistConfig';
import { personalAccountSlice } from 'modules/personalAccount/store/personalAccountSlice';
import { projectsPersistConfig } from 'domains/projects/storage/projectsPersistConfig';
import { referralProgramPersistConfig } from 'modules/referralProgram/storage/referralProgramPersistConfig';
import { referralProgramSlice } from 'modules/referralProgram/store/referralProgramSlice';
import { strapiPersistConfig } from 'modules/strapi/storage/strapiPersistConfig';
import { strapiSlice } from 'modules/strapi/store/strapiSlice';
import { teamsPersistConfig } from 'modules/teams/storage/teamsPersistConfig';
import { teamsSlice } from 'modules/teams/store/teamsSlice';
import { tfaSlice } from 'modules/tfa/store/tfaSlice';
import { themePersistConfig } from 'modules/layout/storage/themePersistConfig';
import { themeSlice } from 'modules/layout/store/themeSlice';
import { userSettingsPersistConfig } from 'domains/userSettings/storage/userSettingsPersistConfig';
import { userSettingsSlice } from 'domains/userSettings/store/userSettingsSlice';
import { walletSlice } from 'modules/wallet/store/walletSlice';
import { dialogSlice } from 'modules/dialogs';
import { ongoingPaymentsSlice } from 'modules/payments/store/ongoingPaymentsSlice';
import { ongoingPaymentsPersistReducer } from 'modules/payments/store/ongoingPaymentsPersistReducer';

import { listenerMiddleware } from './middlewares/listenerMiddleware';
import { notificationListenerMiddleware } from './middlewares/notificationListenerMiddleware';
import { web3Api } from './queries';
import { cryptoApi } from './queries/cryptoApi';

const rootReducer = combineReducers({
  [web3Api.reducerPath]: web3Api.reducer,
  [cryptoApi.reducerPath]: cryptoApi.reducer,
  account: persistReducer(accountPersistConfig, accountSlice.reducer),
  accountTopUp: persistReducer(
    accountTopUpPersistConfig,
    accountTopUpSlice.reducer,
  ),
  auth: persistReducer(authPersistConfig, authSlice.reducer),
  i18n: persistReducer(i18nPersistConfig, i18nSlice.reducer),
  jwtTokenManager: persistReducer(
    jwtTokenManagerPersistConfig,
    jwtTokenManagerSlice.reducer,
  ),
  [dialogSlice.name]: dialogSlice.reducer,
  guardDialog: guardDialogSlice.reducer,
  notifications: notificationSlice.reducer,
  [ongoingPaymentsSlice.name]: ongoingPaymentsPersistReducer,
  personalAccount: persistReducer(
    personalAccountPersistConfig,
    personalAccountSlice.reducer,
  ),
  projects: persistReducer(projectsPersistConfig, projectsSlice.reducer),
  referralProgram: persistReducer(
    referralProgramPersistConfig,
    referralProgramSlice.reducer,
  ),
  strapi: persistReducer(strapiPersistConfig, strapiSlice.reducer),
  teams: persistReducer(teamsPersistConfig, teamsSlice.reducer),
  tfa: tfaSlice.reducer,
  theme: persistReducer(themePersistConfig, themeSlice.reducer),
  userSettings: persistReducer(
    userSettingsPersistConfig,
    userSettingsSlice.reducer,
  ),
  wallet: walletSlice.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(web3Api.middleware)
      .concat(cryptoApi.middleware)
      .prepend(listenerMiddleware.middleware)
      .prepend(notificationListenerMiddleware.middleware),
});

export const persistor = persistStore(store);

export type Store = typeof store;
export type AppDispatch = typeof store.dispatch;
export type GetState = typeof store.getState;
export type RootState = ReturnType<typeof rootReducer>;
